import React, { useState } from 'react';
import { VscReferences } from "react-icons/vsc";
import {
    Dialog,
    DialogTrigger,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    makeStyles,
    shorthands,
} from '@fluentui/react-components';
import { CitationCards } from './CitationCards';
import { IChatMessage } from '../../../libs/models/ChatMessage';

const useStyles = makeStyles({
    dialogWrapper: {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        zIndex: 1000,
    },
    dialogSurface: {
        position: 'relative',
        height: '90vh',
        maxWidth: '100rem',
        ...shorthands.padding('16px'),
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
        backgroundColor: 'white',
        overflow: 'hidden',
    },
    dialogContent: {
        overflowY: 'auto',
        maxHeight: '60vh',
        ...shorthands.padding('16px'),
    },
    buttonWithMargin: {
        marginTop: '10px',
    },
});

interface CitationDialogProps {
    message: IChatMessage; // Mandatory
    documentId?: string; // Optional
    partitionNumber?: string; // Optional
    isOpen?: boolean; // Optional
    onClose?: () => void; // Optional
}

export const CitationDialog: React.FC<CitationDialogProps> = ({
    message,
    documentId = '', // Default value
    partitionNumber = '', // Default value
    isOpen: controlledIsOpen,
    onClose,
}) => {
    //console.log('CitationDialog Props:', { message, documentId, partitionNumber, isOpen: controlledIsOpen });

    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isOpen = controlledIsOpen ?? internalIsOpen; // Controlled or uncontrolled
    const messageCitations = message.citations ?? [];
    //console.log('Message Citations:', messageCitations);

    const classes = useStyles();

    if (messageCitations.length === 0) {
        //console.log('No citations found. Dialog will not render.');
        return null; // Don't render if there are no citations
    }


    
    if(documentId === '' && partitionNumber === '') {
        //console.log('Extracting documentId and partitionNumber from citations.');
 
        documentId = messageCitations[0].link.split('/')[2]; // Extract documentId from the first citation link
    }

    const handleClose = () => {
        //console.log('Dialog is closing.');
        if (controlledIsOpen === undefined) {
            setInternalIsOpen(false); // Close in uncontrolled mode
        }
        if (onClose) {
            onClose(); // Call onClose callback if provided
        }
    };

    return (
            <Dialog
                open={isOpen} // Controlled by the isOpen prop
                onOpenChange={(_, data) => {
                    //console.log('Dialog open state changed:', data.open);
                    if (controlledIsOpen === undefined) {
                        setInternalIsOpen(data.open); // Update internal state in uncontrolled mode
                    }
                    if (!data.open && onClose) {
                        onClose(); // Call onClose when the dialog is closed
                    }
                }}
                modalType="modal"
            >
            <DialogTrigger disableButtonEnhancement>
                <Button
                    appearance="secondary"
                    size="medium"
                    icon={<VscReferences />}
                    className={classes.buttonWithMargin}
                    onClick={() => {
                        if (controlledIsOpen === undefined) {
                            setInternalIsOpen(true); // Open in uncontrolled mode
                        }
                    }}
                >
                    {`${messageCitations.length} ${messageCitations.length === 1 ? 'reference' : 'references'}`}
                </Button>
            </DialogTrigger>
            <div className={classes.dialogWrapper}>
                <DialogSurface className={classes.dialogSurface}>
                    <DialogBody>
                        <DialogTitle>References</DialogTitle>
                        <DialogContent className={classes.dialogContent}>
                        <CitationCards
                            message={message}
                            documentId={documentId} // Pass documentId
                            partitionNumber={partitionNumber} // Pass partitionNumber
                        />
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="secondary" onClick={handleClose}>
                                Close
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </div>
        </Dialog>
    );
};